// Fitting the room shell around what is actually in it. Legacy and imported rooms keep seats outside
// the stored room rectangle (off-center x, beyond the back wall); the shell grows to contain them
// instead of letting seats render in the void, and the overview camera frames the shell, not just the
// seats (a room whose seats all sit at one point has a zero-size seat box). World meters; the shell
// is centered on x = 0 and spans z = [frontZ, depth]. A room with an outline (CIN-15126) is never grown: its
// walls are the operator's design, and the overview frames its bounding box. A sloped or stepped ceiling
// (CIN-15417) grows with the room: its profile is scaled to the new top, its steps stay where they are. Pure.

import { ceilingTop, scaleCeiling } from './outline/ceiling.ts';
import type { RoomGeometry } from './outline/types.ts';

type V3 = { x: number; y: number; z: number };

export type Extent = { minX: number; maxX: number; minZ: number; maxZ: number; top: number };
export type RoomMm = { width: number; depth: number; height: number; geometry?: RoomGeometry | null };

/** Free space kept between the outermost content and a wall the shell had to grow. */
export const FIT_MARGIN_M = 1;
const CEILING_CLEARANCE_M = 0.5;
/** Wall-mounted objects and the screen stand a little into the wall; that is not content outside the room. */
const FIT_TOLERANCE_M = 0.25;

/** Axis-aligned extent of a box (`size` in meters, centered on x/z of `position`, base at `position.y`) turned about y. */
export function boxExtent(position: V3, size: V3, rotationY: number): Extent {
    const cos = Math.abs(Math.cos(rotationY));
    const sin = Math.abs(Math.sin(rotationY));
    const halfX = (size.x * cos + size.z * sin) / 2;
    const halfZ = (size.x * sin + size.z * cos) / 2;
    return {
        minX: position.x - halfX,
        maxX: position.x + halfX,
        minZ: position.z - halfZ,
        maxZ: position.z + halfZ,
        top: position.y + size.y
    };
}

export function unionExtents(extents: Extent[]): Extent | null {
    if (extents.length === 0) return null;
    return extents.reduce((acc, e) => ({
        minX: Math.min(acc.minX, e.minX),
        maxX: Math.max(acc.maxX, e.maxX),
        minZ: Math.min(acc.minZ, e.minZ),
        maxZ: Math.max(acc.maxZ, e.maxZ),
        top: Math.max(acc.top, e.top)
    }));
}

/**
 * The room, grown where the content pokes out of it. Width grows symmetrically (the shell is centered
 * on x = 0), depth toward the back wall, height to clear the tallest thing. A room that already holds
 * its content is returned as is (same object), so memoization downstream stays stable.
 */
export function fitRoomToContent<T extends RoomMm>(room: T, content: Extent | null, groundY: number): T {
    if (!content || room.geometry?.outline) return room;
    const halfWidth = room.width / 2000;
    const reachX = Math.max(Math.abs(content.minX), Math.abs(content.maxX));
    const width = reachX > halfWidth + FIT_TOLERANCE_M ? Math.ceil((reachX + FIT_MARGIN_M) * 2 * 1000) : room.width;
    const depth =
        content.maxZ > room.depth / 1000 + FIT_TOLERANCE_M
            ? Math.ceil((content.maxZ + FIT_MARGIN_M) * 1000)
            : room.depth;
    const ceiling = groundY + room.height / 1000;
    const height =
        content.top > ceiling + FIT_TOLERANCE_M
            ? Math.ceil((content.top + CEILING_CLEARANCE_M - groundY) * 1000)
            : room.height;
    if (width === room.width && depth === room.depth && height === room.height) return room;
    const shaped = room.geometry?.ceiling;
    if (!room.geometry || !shaped) return { ...room, width, depth, height };
    // The profile keeps its shape: scaled when the room had to grow taller, re-read for the new depth otherwise.
    const profile = height === room.height ? shaped : scaleCeiling(shaped, depth, height);
    return {
        ...room,
        width,
        depth,
        height: Math.max(height, ceilingTop(profile, depth)),
        geometry: { ...room.geometry, ceiling: profile }
    };
}

/** The volume the overview camera must show: the shell plus anything in front of the screen wall. */
export function overviewVolume(
    room: RoomMm,
    frontZ: number,
    groundY: number,
    content: Extent | null
): { min: V3; max: V3 } {
    const halfWidth = Math.max(room.width / 2000, content ? Math.max(-content.minX, content.maxX) : 0);
    const minZ = Math.min(frontZ, content ? content.minZ : frontZ);
    const maxZ = Math.max(room.depth / 1000, content ? content.maxZ : 0);
    return {
        min: { x: -halfWidth, y: groundY, z: minZ },
        max: { x: halfWidth, y: groundY + room.height / 1000, z: maxZ }
    };
}
